import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import {
  fetchInputComponent,
  updateInputComponent,
  UpdateInputComponentPayload,
} from '../api/inputApi';
import type {InputComponentActivationResponse} from '../components/InputActivationSection/inputActivationApi';

export interface InputNodeConfig {
  id: string;
  componentId: string;
  workflowId: string;
  type: string;
  name: string;
  description?: string | null;
  storeAs: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  inputSchema: Record<string, unknown> | null;
  inputReferenceKey: string | null;
  constants: Record<string, unknown> | null;
  activation?: InputComponentActivationResponse | null;
  nextComponentId: string | null;
  deleted: boolean;
}

export interface InputNodeState {
  data: InputNodeConfig | null;
  loading: boolean;
  error: string | null;
}

const initialState: InputNodeState = {
  data: null,
  loading: false,
  error: null,
};

export const fetchInputNode = createAsyncThunk(
  'inputNode/fetch',
  async (inputId: string, {rejectWithValue}) => {
    try {
      const response = await fetchInputComponent(inputId);
      if (response.error) {
        return rejectWithValue(
          response.message || 'Failed to fetch input node'
        );
      }
      return response.data;
    } catch (err: unknown) {
      if (err instanceof Error) {
        return rejectWithValue(err.message || 'Failed to fetch input node');
      }
      return rejectWithValue('Failed to fetch input node');
    }
  }
);

export const updateInputNode = createAsyncThunk(
  'inputNode/update',
  async (
    {inputId, payload}: {inputId: string; payload: UpdateInputComponentPayload},
    {rejectWithValue}
  ) => {
    try {
      const response = await updateInputComponent(inputId, payload);

      if (response.error) {
        return rejectWithValue(response);
      }
      return response.data ?? response;
    } catch (err: unknown) {
      if (err instanceof Error) {
        return rejectWithValue(err.message || 'Failed to update input node');
      }
      return rejectWithValue('Failed to update input node');
    }
  }
);

const inputNodeSlice = createSlice({
  name: 'inputNode',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchInputNode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchInputNode.fulfilled,
        (state, action: PayloadAction<InputNodeConfig>) => {
          state.loading = false;
          state.data = action.payload;
        }
      )
      .addCase(fetchInputNode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateInputNode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateInputNode.fulfilled,
        (state, action: PayloadAction<InputNodeConfig>) => {
          state.loading = false;
          state.data = action.payload;
          // Success alert should be dispatched in the component, not here
        }
      )
      .addCase(updateInputNode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default inputNodeSlice.reducer;
export const inputNodeActions = inputNodeSlice.actions;
